import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import FilterPill from "./FilterPill";
import WorkspacePill from "./WorkspacePill";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { useEffect, useState } from "react";
import Conversation from "@/models/conversation/Conversation";
import { FC } from "react";
import useLegislationFilters from "@/hooks/useLegislationFilters";

type Props = {
  selectedConversation: Conversation | undefined;
  isOverview?: boolean;
  availableSources?: string[];
};

const SelectedFilters: FC<Props> = ({
  selectedConversation,
  isOverview = false,
  availableSources,
}) => {
  const [showAsPopover, setShowAsPopover] = useState(false);
  const { selectedSource, setSelectedSource, workspaceContext } =
    useNewConversationFiltersStore();
  const { useGetFilterConfiguration } = useLegislationFilters();
  const fetchedFilterConfiguration = useGetFilterConfiguration();

  const numberOfWorkspaces = Object.values(workspaceContext).length;
  const hasSource = selectedSource !== undefined;
  const hasWorkspaces = numberOfWorkspaces > 0;

  useEffect(() => {
    if (!selectedConversation) {
      return;
    }

    if (numberOfWorkspaces > 3) {
      setShowAsPopover(true);
      return;
    }
    if (hasSource) {
      setShowAsPopover(true);
      return;
    }
    setShowAsPopover(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceContext, selectedSource, selectedConversation]);

  const handlePopoverClose = () => {
    if (hasSource) {
      return;
    }
    if (hasWorkspaces && numberOfWorkspaces > 3) {
      return;
    }

    if (!hasSource || !hasWorkspaces || numberOfWorkspaces <= 3) {
      setShowAsPopover(false);
    }
  };

  // Validate source against availableSources when conversation is selected
  useEffect(() => {
    if (
      selectedConversation &&
      selectedSource &&
      availableSources &&
      Array.isArray(availableSources)
    ) {
      if (!availableSources.includes(selectedSource)) {
        // Source is no longer available, clear it
        setSelectedSource(undefined);
      }
    }
  }, [
    selectedConversation,
    selectedSource,
    availableSources,
    setSelectedSource,
  ]);

  // Helper function to get displayName from sourceName
  const getSourceDisplayName = (sourceName: string): string => {
    if (
      fetchedFilterConfiguration.isFetched &&
      fetchedFilterConfiguration.data
    ) {
      const sourceDef = fetchedFilterConfiguration.data.sources[sourceName];
      return sourceDef?.displayName || sourceName;
    }
    return sourceName;
  };

  return (
    <div className="flex flex-wrap gap-2 py-1.5 px-2">
      {showAsPopover && !isOverview ? (
        <Popover onOpenChange={handlePopoverClose}>
          <PopoverTrigger asChild>
            <Badge
              variant="outline"
              className={`flex items-center space-x-1 px-2 py-1 border-primary cursor-pointer`}
            >
              <span className="truncate max-w-[150px] font-medium">
                {hasSource ? "1" : numberOfWorkspaces}{" "}
                {hasSource ? "kilde" : "kilder"} valgt
              </span>
            </Badge>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-2 w-fit flex-wrap">
            {selectedSource && (
              <FilterPill
                source={selectedSource}
                displayName={getSourceDisplayName(selectedSource)}
                onRemoveSource={() => setSelectedSource(undefined)}
                inPopover={true}
              />
            )}
            {Object.entries(workspaceContext).map(
              ([workspaceId, selectedIds]) => (
                <WorkspacePill
                  key={workspaceId}
                  workspaceId={workspaceId}
                  selectedIds={selectedIds.map((doc) => doc.documentId)}
                  inPopover={true}
                />
              )
            )}
          </PopoverContent>
        </Popover>
      ) : (
        <>
          {selectedSource && (
            <FilterPill
              source={selectedSource}
              displayName={getSourceDisplayName(selectedSource)}
              onRemoveSource={() => setSelectedSource(undefined)}
              isOverview={isOverview}
            />
          )}
          {Object.entries(workspaceContext).map(
            ([workspaceId, selectedIds]) => (
              <WorkspacePill
                key={workspaceId}
                workspaceId={workspaceId}
                selectedIds={selectedIds.map((doc) => doc.documentId)}
                isOverview={isOverview}
              />
            )
          )}
        </>
      )}
    </div>
  );
};

export default SelectedFilters;
